import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { doctorLogin, doctorRegister, doctorSendRegisterOtp, staffLogin, forgotPasswordSendOTP, forgotPasswordReset } from '../../services/doctorService';
import { getStaffFirstPage } from '../../utils/staffUtils';
import AppLogo from '../../components/common/AppLogo';

// ── Small input component ─────────────────────────────────────────────────
function Field({ label, type='text', value, onChange, placeholder, icon, right }) {
  const [show, setShow] = useState(false);
  const isPass = type === 'password';
  return (
    <div>
      {label && <label className="input-label">{label}</label>}
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
            <i className={`fa-solid ${icon} text-sm`} />
          </div>
        )}
        <input
          type={isPass && show ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={`input ${icon ? 'pl-9' : ''} ${isPass ? 'pr-10' : ''}`}
        />
        {isPass && (
          <button type="button" onClick={() => setShow(!show)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
            <i className={`fa-solid ${show ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
          </button>
        )}
        {right && <div className="absolute right-3 top-1/2 -translate-y-1/2">{right}</div>}
      </div>
    </div>
  );
}

// ── Forgot Password Flow ──────────────────────────────────────────────────
function ForgotPasswordModal({ onClose }) {
  const [step, setStep]           = useState(1); // 1=identity, 2=OTP+newPass
  const [phone, setPhone]         = useState('');
  const [clinicName, setClinicName] = useState('');
  const [otp, setOtp]             = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [cooldown, setCooldown]   = useState(0);

  // Resend cooldown timer
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendOtp = async () => {
    if (!phone || phone.length !== 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    if (!clinicName.trim()) { toast.error('Enter your clinic name'); return; }
    setLoading(true);
    try {
      await forgotPasswordSendOTP({ phone, clinicName: clinicName.trim() });
      toast.success('OTP sent to +91 ' + phone);
      setStep(2);
      setCooldown(60);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await forgotPasswordSendOTP({ phone, clinicName: clinicName.trim() });
      toast.success('OTP resent!');
      setCooldown(60);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  const resetPass = async () => {
    if (!otp || otp.length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    if (!newPass || newPass.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await forgotPasswordReset({ phone, clinicName: clinicName.trim(), otp, newPassword: newPass });
      toast.success('Password reset successfully! Please login.');
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}>
        <div className="bg-white w-full max-w-sm rounded-t-3xl sm:rounded-2xl p-6 space-y-4"
          onClick={e => e.stopPropagation()}>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-text-primary font-heading">
                {step === 1 ? 'Reset Password' : 'Verify & Reset'}
              </h3>
              <div className="flex gap-1 mt-1">
                {[1,2].map(s => (
                  <div key={s} className={`h-1 rounded-full transition-all ${s <= step ? 'bg-primary w-8' : 'bg-border w-4'}`} />
                ))}
              </div>
            </div>
            <button onClick={onClose} className="btn-icon w-8 h-8">
              <i className="fa-solid fa-xmark text-text-secondary" />
            </button>
          </div>

          {step === 1 ? (
            <>
              <p className="text-sm text-text-muted">Enter your registered phone number and clinic name to receive an OTP</p>
              <Field label="Phone Number" type="tel"
                value={phone}
                onChange={v => setPhone(v.replace(/\D/g,'').slice(0,10))}
                placeholder="10-digit mobile number" icon="fa-phone" />
              <Field label="Clinic Name"
                value={clinicName}
                onChange={setClinicName}
                placeholder="Your registered clinic name" icon="fa-hospital" />
              <button onClick={sendOtp} disabled={loading || !phone || !clinicName} className="btn-primary">
                {loading
                  ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Sending OTP...</span>
                  : <span><i className="fa-solid fa-paper-plane mr-2" />Send OTP</span>}
              </button>
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-success text-sm" />
                <div>
                  <p className="text-xs font-semibold text-green-800">OTP sent to +91 {phone}</p>
                  <button onClick={resendOtp} disabled={cooldown > 0 || loading}
                    className="text-[10px] text-primary font-medium">
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <div>
                <label className="input-label">6-digit OTP</label>
                <input type="tel" value={otp} maxLength={6}
                  onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="Enter OTP"
                  className="input text-center tracking-[0.5em] text-lg font-bold" />
              </div>

              <div className="relative">
                <Field label="New Password" type={showPass ? 'text' : 'password'}
                  value={newPass} onChange={setNewPass}
                  placeholder="Min 6 characters" icon="fa-lock" />
                <button type="button" onMouseDown={e => e.preventDefault()}
                  onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 bottom-3 text-text-muted text-sm">
                  <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>

              <Field label="Confirm Password" type={showPass ? 'text' : 'password'}
                value={confirmPass} onChange={setConfirmPass}
                placeholder="Re-enter password" icon="fa-lock-open" />

              {confirmPass && newPass !== confirmPass && (
                <p className="text-xs text-danger flex items-center gap-1">
                  <i className="fa-solid fa-circle-xmark" /> Passwords do not match
                </p>
              )}

              <button onClick={resetPass}
                disabled={loading || otp.length !== 6 || !newPass || newPass !== confirmPass}
                className="btn-primary">
                {loading
                  ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Resetting...</span>
                  : <span><i className="fa-solid fa-key mr-2" />Reset Password</span>}
              </button>
              <button onClick={() => { setStep(1); setOtp(''); }} className="w-full text-sm text-text-muted text-center">
                ← Change phone number
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function DoctorLoginPage() {
  const navigate         = useNavigate();
  const location         = useLocation();
  const { loginSuccess } = useAuth();
  const [tab, setTab]    = useState('login');
  const [loading, setLoading]   = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const [loginForm, setLoginForm] = useState({ clinicName: '', phone: '', password: '' });
  const [regForm, setRegForm]     = useState({ name: '', phone: '', clinicName: '', password: '', role: 'Doctor' });
  const [regOtpSent, setRegOtpSent] = useState(false);
  const [regOtp, setRegOtp]         = useState('');
  const [staffForm, setStaffForm] = useState({ clinicName: '', username: '', password: '' });

  const setL = (k, v) => setLoginForm(p => ({ ...p, [k]: v }));
  const setR = (k, v) => setRegForm(p => ({ ...p, [k]: v }));
  const setS = (k, v) => setStaffForm(p => ({ ...p, [k]: v }));

  const handleLogin = async () => {
    const { clinicName, phone, password } = loginForm;
    if (!clinicName || !phone || !password) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      const res = await doctorLogin({ clinicName, phoneOrEmail: phone, password });
      loginSuccess(res.token, res.doctor, 'doctor');
      toast.success(`Welcome, ${res.doctor.name}!`);
      // Return to where doctor was before hitting login wall
      // Priority 1: router state (set when DoctorRoute redirects unauthenticated user)
      const from = location.state?.from;
      if (from && from !== '/doctor/login' && !from.startsWith('/admin')) {
        navigate(from, { replace: true }); return;
      }
      // Priority 2: sessionStorage origin (set when doctor manually clicked Login)
      const savedOrigin = sessionStorage.getItem('loginOrigin');
      if (savedOrigin && savedOrigin !== '/doctor/login') {
        sessionStorage.removeItem('loginOrigin');
        navigate(savedOrigin, { replace: true }); return;
      }
      navigate('/doctor/dashboard', { replace: true });
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const sendRegOtp = async () => {
    const { phone, clinicName } = regForm;
    if (!phone || phone.replace(/\D/g,'').length < 10) { toast.error('Enter valid 10-digit phone'); return; }
    if (!clinicName.trim()) { toast.error('Clinic name required'); return; }
    setLoading(true);
    try {
      await doctorSendRegisterOtp({ phone: phone.replace(/\D/g,'').slice(-10) });
      setRegOtpSent(true);
      toast.success('OTP sent to +91 ' + phone.replace(/\D/g,'').slice(-10));
    } catch (err) { toast.error(err.message || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const handleRegister = async () => {
    const { name, phone, clinicName, password, role } = regForm;
    if (!name || !phone || !clinicName || !password) { toast.error('All fields required'); return; }
    if (!/^[6-9]\d{9}$/.test(phone)) { toast.error('Enter valid 10-digit mobile number'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      if (!regOtp || regOtp.length !== 6) { toast.error('Enter the 6-digit OTP sent to your phone'); return; }
      const res = await doctorRegister({ name, phone, clinicName, password, role, otp: regOtp });
      loginSuccess(res.token, res.doctor, 'doctor');
      toast.success('Account created! Complete your profile.');
      navigate('/doctor/profile', { replace: true });
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleStaffLogin = async () => {
    const { clinicName, username, password } = staffForm;
    if (!clinicName || !username || !password) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      const res = await staffLogin({ clinicName, username, password });
      // Merge key doctor fields into staffUser so dashboard has fee, clinic info etc.
      const staffUser = {
        ...res.staff,
        name:            res.staff.username,
        clinicName:      res.doctor.clinicName,
        permissions:     res.staff.permissions,
        consultationFee: res.doctor.consultationFee || 0,
        profileImg:      res.doctor.profileImg || null,
        speciality:      res.doctor.speciality  || '',
        doctorId:        res.doctor._id,
        oldPatientDiscount: res.doctor.oldPatientDiscount || {},
      };
      loginSuccess(res.token, staffUser, 'staff');
      toast.success(`Welcome, ${res.staff.username}!`);
      // Priority 1: router state (set when DoctorRoute redirects unauthenticated user)
      const from = location.state?.from;
      if (from && from !== '/doctor/login' && !from.startsWith('/admin')) {
        navigate(from, { replace: true }); return;
      }
      // Priority 2: sessionStorage origin
      const savedOrigin = sessionStorage.getItem('loginOrigin');
      if (savedOrigin && savedOrigin !== '/doctor/login') {
        sessionStorage.removeItem('loginOrigin');
        navigate(savedOrigin, { replace: true }); return;
      }
      // Priority 3: first permitted page for staff
      const firstPage = getStaffFirstPage(res.staff.permissions || {});
      navigate(firstPage || '/doctor/dashboard', { replace: true });
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const TABS = [
    { key: 'login',    label: 'Login',    icon: 'fa-right-to-bracket' },
    { key: 'register', label: 'Sign Up',  icon: 'fa-user-plus' },
    { key: 'staff',    label: 'Staff',    icon: 'fa-users' },
  ];

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-border bg-white gap-3">
        <button onClick={() => navigate('/')} className="btn-icon">
          <i className="fa-solid fa-arrow-left text-text-primary" />
        </button>
        <AppLogo size="sm" />
      </div>

      <div className="flex-1 px-5 py-6 max-w-md mx-auto w-full">
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center">
            <i className="fa-solid fa-user-doctor text-primary text-xl" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text-primary font-heading">Doctor Portal</h1>
            <p className="text-xs text-text-muted">Manage your practice online</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-surface border border-border rounded-xl p-1 mb-6">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                tab === t.key ? 'bg-white text-primary shadow-sm' : 'text-text-muted'
              }`}>
              <i className={`fa-solid ${t.icon} text-xs`} />{t.label}
            </button>
          ))}
        </div>

        {/* ── Login ── */}
        {tab === 'login' && (
          <div className="space-y-4">
            <Field label="Clinic Name" value={loginForm.clinicName} onChange={v => setL('clinicName', v)}
              placeholder="Your registered clinic name" icon="fa-hospital" />
            <Field label="Phone Number" type="tel" value={loginForm.phone}
              onChange={v => setL('phone', v.replace(/\D/g,'').slice(0,10))}
              placeholder="Registered mobile number" icon="fa-phone" />
            <Field label="Password" type="password" value={loginForm.password} onChange={v => setL('password', v)}
              placeholder="Your password" icon="fa-lock" />
            <div className="flex justify-end">
              <button onClick={() => setShowForgot(true)}
                className="text-xs text-primary font-medium hover:underline">
                Forgot Password?
              </button>
            </div>
            <button onClick={handleLogin} disabled={loading} className="btn-primary">
              {loading ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Logging in...</span> : <span><i className="fa-solid fa-right-to-bracket mr-2" />Login</span>}
            </button>
            <p className="text-center text-xs text-text-muted">
              New doctor?{' '}
              <button onClick={() => setTab('register')} className="text-primary font-semibold">Sign Up</button>
            </p>
          </div>
        )}

        {/* ── Register ── */}
        {tab === 'register' && (
          <div className="space-y-4">
            {/* Role dropdown */}
            <div>
              <label className="input-label">Role *</label>
              <div className="relative">
                <i className="fa-solid fa-user-doctor absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
                <select value={regForm.role} onChange={e => setR('role', e.target.value)}
                  className="input pl-10 appearance-none">
                  <option value="Doctor">Doctor</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="H">Hospital Staff (H)</option>
                  <option value="P">Physician (P)</option>
                  <option value="R">Resident (R)</option>
                </select>
                <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-text-muted text-xs pointer-events-none" />
              </div>
            </div>
            <Field label="Full Name *" value={regForm.name} onChange={v => setR('name', v)}
              placeholder="Dr. Your Name" icon="fa-user" />
            <Field label="Phone Number *" type="tel" value={regForm.phone}
              onChange={v => { setR('phone', v.replace(/\D/g,'').slice(0,10)); setRegOtpSent(false); setRegOtp(''); }}
              placeholder="10-digit mobile number" icon="fa-phone" />
            <Field label="Clinic Name *" value={regForm.clinicName} onChange={v => setR('clinicName', v)}
              placeholder="Your clinic name" icon="fa-hospital" />
            <Field label="Password *" type="password" value={regForm.password} onChange={v => setR('password', v)}
              placeholder="Min 6 characters" icon="fa-lock" />

            {/* Step: Verify phone via OTP */}
            {!regOtpSent ? (
              <button onClick={sendRegOtp}
                disabled={loading || regForm.phone.length < 10 || !regForm.clinicName.trim() || !regForm.name.trim()}
                className="btn-secondary w-full flex items-center justify-center gap-2">
                {loading
                  ? <><i className="fa-solid fa-spinner animate-spin" />Sending OTP...</>
                  : <><i className="fa-solid fa-mobile-screen" />Send OTP to Verify Phone</>}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-success text-sm" />
                  <p className="text-xs text-green-700 font-medium">OTP sent to +91 {regForm.phone}</p>
                  <button onClick={sendRegOtp} disabled={loading} className="ml-auto text-xs text-primary font-semibold">Resend</button>
                </div>
                <div>
                  <label className="input-label">Enter 6-digit OTP <span className="text-danger">*</span></label>
                  <input type="tel" value={regOtp} maxLength={6}
                    onChange={e => setRegOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                    placeholder="Enter OTP"
                    className="input text-center tracking-[0.5em] text-lg font-bold" />
                </div>
              </div>
            )}

            <button onClick={handleRegister} disabled={loading || !regOtpSent || regOtp.length !== 6} className="btn-primary">
              {loading ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Creating...</span> : <span><i className="fa-solid fa-user-plus mr-2" />Create Account</span>}
            </button>
            <p className="text-xs text-text-muted text-center leading-relaxed">
              By signing up you agree to our{' '}
              <span className="text-primary cursor-pointer">Terms & Conditions</span>
            </p>
          </div>
        )}

        {/* ── Staff ── */}
        {tab === 'staff' && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-xs text-info font-medium flex gap-2">
              <i className="fa-solid fa-circle-info mt-0.5 flex-shrink-0" />
              Staff accounts are created by the doctor. Contact your doctor for credentials.
            </div>
            <Field label="Clinic Name" value={staffForm.clinicName} onChange={v => setS('clinicName', v)}
              placeholder="Clinic name" icon="fa-hospital" />
            <Field label="Username" value={staffForm.username} onChange={v => setS('username', v)}
              placeholder="Your staff username" icon="fa-user" />
            <Field label="Password" type="password" value={staffForm.password} onChange={v => setS('password', v)}
              placeholder="Your password" icon="fa-lock" />
            <button onClick={handleStaffLogin} disabled={loading} className="btn-primary">
              {loading ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Logging in...</span> : <span><i className="fa-solid fa-right-to-bracket mr-2" />Staff Login</span>}
            </button>
          </div>
        )}
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
